import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row
} from 'reactstrap';
import LoadingComponent from 'pages/components/Loading';
import { warningAlert, successAlert, infoAlert } from 'pages/components/SweetAlert';
import {
  addClienteBot,
  addInstalacionBot,
  addPagoInstaladorBot,
  addServicioBot,
  entregarEquiposClienteBot,
  getAuthUserBot,
  getDebtBot,
  getEventosInstalador,
  getEmpleadosBot,
  getMisEquiposByEmpleado,
  getPlanesBot,
  updateEventoBot
} from 'services/InstaladoresBotService';

const STATUS = {
  NONE: '',
  SUCCESS: 'success',
  DANGER: 'danger',
  INFO: 'info'
};

const normalizeMac = (mac) => (mac || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const currentLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const InstaladoresBot = () => {
  const [loading, setLoading] = useState(false);
  const [alertSweet, setAlert] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [instaladores, setInstaladores] = useState([]);
  const [instaladorId, setInstaladorId] = useState('');
  const [instaladorNombre, setInstaladorNombre] = useState('');
  const [busquedaInstalador, setBusquedaInstalador] = useState('');
  const [dropdownInstalador, setDropdownInstalador] = useState(false);
  const [paginaInstalador, setPaginaInstalador] = useState(0);
  const dropdownRef = useRef(null);
  const ITEMS_POR_PAGINA = 6;
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState('');
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
  const [pudoInstalar, setPudoInstalar] = useState(null);
  const [comentarioNoInstalo, setComentarioNoInstalo] = useState('');
  const [tipoInstalacion, setTipoInstalacion] = useState('');
  const [macIngresada, setMacIngresada] = useState('');
  const [equipoInstalado, setEquipoInstalado] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [deseaCambiarPlan, setDeseaCambiarPlan] = useState(null);
  const [planIdSeleccionado, setPlanIdSeleccionado] = useState(null);
  const [resultadoFinal, setResultadoFinal] = useState(null);
  const [clienteCreado, setClienteCreado] = useState(null);

  const [devolvioRouter, setDevolvioRouter] = useState(null);
  const [utilizoRouterAdicional, setUtilizoRouterAdicional] = useState(null);
  const [macRouterAdicional, setMacRouterAdicional] = useState('');
  const [routerAdicionalOk, setRouterAdicionalOk] = useState(null);

  const [formaPago, setFormaPago] = useState(null);
  const [deudaCliente, setDeudaCliente] = useState(null);
  const [pagoRegistrado, setPagoRegistrado] = useState(false);

  const instaladoresFiltrados = useMemo(() => {
    if (!busquedaInstalador.trim()) return instaladores;
    const q = busquedaInstalador.toLowerCase();
    return instaladores.filter((e) =>
      `${e.nombre} ${e.apellido} ${e.dni}`.toLowerCase().includes(q)
    );
  }, [instaladores, busquedaInstalador]);

  const esperaEvento = useMemo(() => eventoSeleccionado?.espera || null, [eventoSeleccionado]);
  const planActual = useMemo(() => {
    if (!planes.length || !planIdSeleccionado) return null;
    return planes.find((p) => p.id === planIdSeleccionado) || null;
  }, [planes, planIdSeleccionado]);

  const cargarEventos = async (empId) => {
    const hoy = currentLocalDate();
    const params = { desde: hoy, hasta: hoy };
    if (empId) {
      params.empleadoId = empId;
    }
    const res = await getEventosInstalador(params);
    if (!res?.ok || !Array.isArray(res.data)) {
      setEventos([]);
      warningAlert(setAlert, 'Error', 'No se pudieron cargar los eventos del día.');
      return;
    }
    const pendientes = res.data.filter((ev) => ev.completado === false && ev.espera);
    setEventos(pendientes);
  };

  const cargarInicial = async () => {
    setLoading(true);
    const auth = await getAuthUserBot();
    if (!auth?.ok || !auth.usuario) {
      warningAlert(setAlert, 'Error', 'No se pudo validar el usuario logueado.');
      setLoading(false);
      return;
    }
    setAuthUser(auth.usuario);
    const lista = await getEmpleadosBot();
    setInstaladores(lista);
    setLoading(false);
  };

  useEffect(() => {
    cargarInicial();
  }, []);

  useEffect(() => {
    const handleClickFuera = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownInstalador(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  const handleBusquedaInstalador = (e) => {
    setBusquedaInstalador(e.target.value);
    setPaginaInstalador(0);
    setDropdownInstalador(true);
  };

  const handleSeleccionarInstalador = async (inst) => {
    setInstaladorId(String(inst.id));
    setInstaladorNombre(`${inst.nombre} ${inst.apellido} (${inst.dni})`);
    setBusquedaInstalador('');
    setDropdownInstalador(false);
    setPaginaInstalador(0);
    setEventos([]);
    setEventoId('');
    setEventoSeleccionado(null);
    setLoading(true);
    await cargarEventos(inst.id);
    setLoading(false);
  };

  const aplicarSeleccionEvento = async (ev) => {
    const planIdInicial = ev.espera?.planId || 2;
    const listadoPlanes = await getPlanesBot();
    setPlanes(listadoPlanes);
    setPlanIdSeleccionado(planIdInicial);
    setEventoSeleccionado(ev);
    setPudoInstalar(null);
    setComentarioNoInstalo('');
    setTipoInstalacion('');
    setMacIngresada('');
    setEquipoInstalado(null);
    setDeseaCambiarPlan(null);
    setResultadoFinal(null);
    setClienteCreado(null);
    setDevolvioRouter(null);
    setUtilizoRouterAdicional(null);
    setMacRouterAdicional('');
    setRouterAdicionalOk(null);
    setFormaPago(null);
    setDeudaCliente(null);
    setPagoRegistrado(false);
    // evento seleccionado - sin alerta
  };

  const seleccionarEventoDirecto = async (ev) => {
    setLoading(true);
    await aplicarSeleccionEvento(ev);
    setLoading(false);
  };

  const finalizarNoInstalado = async () => {
    if (!eventoSeleccionado) return;
    if (!comentarioNoInstalo.trim()) {
      warningAlert(setAlert, 'Atención', 'Debes indicar por qué no se pudo instalar.');
      return;
    }
    setLoading(true);
    const update = await updateEventoBot({
      id: eventoSeleccionado.id,
      comentario: comentarioNoInstalo,
      className: 'bg-default',
      completado: true
    });
    setLoading(false);
    if (update?.ok) {
      successAlert(setAlert, 'Listo', 'Evento guardado correctamente.');
      await cargarEventos(authUser);
    } else {
      warningAlert(setAlert, 'Error', update?.mensaje || 'Error al actualizar el evento.');
    }
  };

  const validarMac = async () => {
    if (!instaladorId) {
      warningAlert(setAlert, 'Atención', 'Seleccioná un instalador antes de validar la MAC.');
      return;
    }
    const mac = normalizeMac(macIngresada);
    if (!mac) {
      warningAlert(setAlert, 'Atención', 'Debes ingresar una MAC válida.');
      return;
    }

    setLoading(true);
    const equipos = await getMisEquiposByEmpleado(parseInt(instaladorId, 10));
    setLoading(false);
    const equipo = (equipos || []).find((e) => normalizeMac(e.macAddress) === mac);
    if (!equipo) {
      setEquipoInstalado(null);
      warningAlert(setAlert, 'MAC no encontrada', 'La MAC no está asignada al instalador seleccionado.');
      return;
    }

    setEquipoInstalado(equipo);
    successAlert(setAlert, 'MAC Válida', `Equipo validado: ${equipo.macAddress}`);
  };

  const confirmarAlta = async () => {
    if (!eventoSeleccionado || !esperaEvento || !equipoInstalado || !planIdSeleccionado) return;

    const {
      nombre,
      apellido,
      telefono,
      dni,
      direccion,
      email,
      lat,
      lng,
      zonaId
    } = esperaEvento;

    if (!dni || !nombre || !apellido) {
      warningAlert(setAlert, 'Datos incompletos', 'Faltan datos obligatorios de la espera para crear el cliente.');
      return;
    }

    setLoading(true);
    const nuevoCliente = await addClienteBot({
      userIdent: dni,
      firstName: nombre,
      lastName: apellido,
      email,
      phone: telefono,
      name: authUser?.usuario || 'Sistema'
    });

    if (!nuevoCliente?.ok || !nuevoCliente?.cliente?.id) {
      setLoading(false);
      warningAlert(setAlert, 'Error', nuevoCliente?.mensaje || 'No se pudo crear el cliente.');
      return;
    }

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    const nuevoServicio = await addServicioBot({
      firstName: nuevoCliente.cliente.firstName,
      lastName: nuevoCliente.cliente.lastName,
      clientId: nuevoCliente.cliente.id,
      planId: planIdSeleccionado,
      street1: direccion,
      city: null,
      macEquipo: equipoInstalado.macAddress,
      addressGpsLat: Number.isNaN(latNum) ? null : latNum,
      addressGpsLon: Number.isNaN(lngNum) ? null : lngNum,
      zonaId
    });

    if (!nuevoServicio?.ok) {
      setLoading(false);
      warningAlert(setAlert, 'Error', nuevoServicio?.mensaje || 'No se pudo crear el servicio.');
      return;
    }

    if (tipoInstalacion === 'FIBRA') {
      await addInstalacionBot({
        clientId: nuevoCliente.cliente.id,
        amount: 12500,
        descripcion: 'Instalacion'
      });
    }

    await updateEventoBot({
      id: eventoSeleccionado.id,
      className: 'bg-default',
      completado: true
    });

    setClienteCreado(nuevoCliente.cliente);
    setResultadoFinal({
      clienteId: nuevoCliente.cliente.id,
      servicioId: nuevoServicio?.servicio?.id || null
    });

    if (tipoInstalacion === 'FIBRA' && nuevoCliente.cliente.id > 6548) {
      setDevolvioRouter(true);
      const debtRes = await getDebtBot(nuevoCliente.cliente.id);
      setDeudaCliente(debtRes?.debt || 0);
    }

    setLoading(false);
    successAlert(setAlert, 'Alta registrada', 'Cliente y servicio creados. Completa los pasos siguientes.');
    await cargarEventos(authUser);
  };

  const cargarDeuda = async (clientId) => {
    const id = clientId || clienteCreado?.id;
    if (!id) return;
    setLoading(true);
    const debtRes = await getDebtBot(id);
    setLoading(false);
    if (debtRes?.debt) {
      setDeudaCliente(debtRes.debt);
    } else {
      setDeudaCliente(0);
    }
  };

  const handleDevolvioRouter = async (devolvio) => {
    setDevolvioRouter(devolvio);
    if (!devolvio && clienteCreado) {
      setLoading(true);
      await addInstalacionBot({
        clientId: clienteCreado.id,
        amount: 3400,
        descripcion: 'Router'
      });
      setLoading(false);
    }
    await cargarDeuda();
  };

  const handleRouterAdicional = async (utilizo) => {
    setUtilizoRouterAdicional(utilizo);
    if (!utilizo) {
      await cargarDeuda();
    }
  };

  const validarMacRouterAdicional = async () => {
    if (!instaladorId || !clienteCreado) return;
    const mac = normalizeMac(macRouterAdicional);
    if (!mac) {
      warningAlert(setAlert, 'Atención', 'Ingresa la MAC del router adicional.');
      return;
    }
    setLoading(true);
    const equipos = await getMisEquiposByEmpleado(parseInt(instaladorId, 10));
    const equipo = (equipos || []).find((e) => normalizeMac(e.macAddress) === mac);
    if (!equipo) {
      setLoading(false);
      warningAlert(setAlert, 'MAC no encontrada', 'La MAC no está asignada al instalador seleccionado.');
      return;
    }
    const entrega = await entregarEquiposClienteBot([mac], clienteCreado.id);
    setLoading(false);
    if (entrega?.ok !== false) {
      setRouterAdicionalOk(true);
      successAlert(setAlert, 'Listo', 'Router adicional entregado al cliente.');
      setTimeout(() => cargarDeuda(), 1500);
    } else {
      warningAlert(setAlert, 'Error', 'Error al entregar el router adicional.');
    }
  };

  const handlePagoEfectivo = async () => {
    if (!clienteCreado || !deudaCliente) return;
    setLoading(true);
    const pago = await addPagoInstaladorBot(clienteCreado.id, deudaCliente);
    setLoading(false);
    if (pago?.ok) {
      setPagoRegistrado(true);
      successAlert(setAlert, '¡Pago registrado!', 'El pago fue registrado correctamente. Flujo finalizado.');
    } else {
      warningAlert(setAlert, 'Error en el pago', pago?.mensaje || 'Error al registrar el pago.');
    }
  };

  return (
    <Container className="mt-3" fluid>
      {alertSweet}
      <LoadingComponent Visible={loading} />

      <Card>
        <CardHeader>
          <h3 className="mb-0">Migracion flujo Instaladores Bot</h3>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={5}>
              <FormGroup>
                <label>Instalador:</label>
                {instaladorNombre && (
                  <div className="mb-1 d-flex align-items-center">
                    <Badge color="primary" className="py-1 px-2 mr-2" style={{ fontSize: '0.85em' }}>
                      {instaladorNombre}
                    </Badge>
                    <Button
                      size="sm"
                      color="link"
                      className="p-0 text-danger"
                      onClick={() => {
                        setInstaladorId('');
                        setInstaladorNombre('');
                        setBusquedaInstalador('');
                        setEventos([]);
                        setEventoId('');
                        setEventoSeleccionado(null);
                      }}
                    >
                      <i className="fas fa-times" />
                    </Button>
                  </div>
                )}
                <div ref={dropdownRef}>
                  <Input
                    type="text"
                    placeholder="Buscar por nombre, apellido o DNI..."
                    value={busquedaInstalador}
                    onChange={handleBusquedaInstalador}
                    onFocus={() => setDropdownInstalador(true)}
                    autoComplete="off"
                  />
                  {dropdownInstalador && (
                    <div
                      className="border rounded bg-white shadow"
                      style={{ position: 'absolute', zIndex: 1000, width: '360px', maxWidth: '90vw' }}
                    >
                      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {instaladoresFiltrados.length === 0 ? (
                          <div className="px-3 py-2 text-muted">
                            <small>Sin resultados</small>
                          </div>
                        ) : (
                          instaladoresFiltrados
                            .slice(paginaInstalador * ITEMS_POR_PAGINA, (paginaInstalador + 1) * ITEMS_POR_PAGINA)
                            .map((inst) => (
                              <div
                                key={inst.id}
                                className="px-3 py-2"
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: instaladorId === String(inst.id) ? '#e8f4fd' : 'transparent',
                                  borderBottom: '1px solid #f0f0f0'
                                }}
                                onMouseEnter={(e) => { if (instaladorId !== String(inst.id)) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                onMouseLeave={(e) => { if (instaladorId !== String(inst.id)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                onClick={() => handleSeleccionarInstalador(inst)}
                              >
                                <span>{`${inst.nombre} ${inst.apellido}`}</span>
                                <br />
                                <small className="text-muted">{inst.dni}</small>
                              </div>
                            ))
                        )}
                      </div>
                      {instaladoresFiltrados.length > ITEMS_POR_PAGINA && (
                        <div className="d-flex justify-content-between align-items-center px-3 py-1 border-top bg-light">
                          <Button
                            size="sm"
                            color="link"
                            className="p-0"
                            disabled={paginaInstalador === 0}
                            onClick={() => setPaginaInstalador((p) => p - 1)}
                          >
                            <i className="fas fa-chevron-left" />
                          </Button>
                          <small className="text-muted">
                            {paginaInstalador + 1} / {Math.ceil(instaladoresFiltrados.length / ITEMS_POR_PAGINA)}
                          </small>
                          <Button
                            size="sm"
                            color="link"
                            className="p-0"
                            disabled={(paginaInstalador + 1) * ITEMS_POR_PAGINA >= instaladoresFiltrados.length}
                            onClick={() => setPaginaInstalador((p) => p + 1)}
                          >
                            <i className="fas fa-chevron-right" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>1) Selecciona el evento completado</Label>
                <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {!instaladorId ? (
                    <small className="text-muted">Seleccione un instalador para ver sus eventos.</small>
                  ) : eventos.length === 0 ? (
                    <small>No hay eventos pendientes con espera asociada.</small>
                  ) : (
                    eventos.map((ev) => (
                      <div
                        key={ev.id}
                        className="mb-2 p-2 rounded"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: eventoId === String(ev.id) ? '#e8f4fd' : 'transparent',
                          border: eventoId === String(ev.id) ? '1px solid #5e72e4' : '1px solid transparent'
                        }}
                        onClick={() => {
                          setEventoId(String(ev.id));
                          seleccionarEventoDirecto(ev);
                        }}
                      >
                        <Badge color={eventoId === String(ev.id) ? 'primary' : 'info'} className="mr-2">{`Id ${ev.id}`}</Badge>
                        <span>{ev?.espera?.direccion || '-'}</span>
                        <br />
                        <small>{`${ev?.espera?.nombre || ''} ${ev?.espera?.apellido || ''}`}</small>
                      </div>
                    ))
                  )}
                </div>
              </FormGroup>
            </Col>
          </Row>

          {eventoSeleccionado ? (
            <>
              <hr />
              <h4>2) Pudiste instalar?</h4>
              <Button
                color={pudoInstalar === true ? 'success' : 'secondary'}
                className="mr-2"
                onClick={() => setPudoInstalar(true)}
              >
                SI
              </Button>
              <Button
                color={pudoInstalar === false ? 'danger' : 'secondary'}
                onClick={() => setPudoInstalar(false)}
              >
                NO
              </Button>
            </>
          ) : null}

          {pudoInstalar === false ? (
            <>
              <hr />
              <FormGroup>
                <Label>3) Por que no se pudo instalar?</Label>
                <Input
                  type="textarea"
                  value={comentarioNoInstalo}
                  onChange={(e) => setComentarioNoInstalo(e.target.value)}
                />
              </FormGroup>
              <Button color="warning" onClick={finalizarNoInstalado}>
                Guardar y cerrar evento
              </Button>
            </>
          ) : null}

          {pudoInstalar === true ? (
            <>
              <hr />
              <h4>3) Que tipo de instalacion realizaste?</h4>
              <Button
                color={tipoInstalacion === 'FIBRA' ? 'success' : 'secondary'}
                className="mr-2"
                onClick={() => setTipoInstalacion('FIBRA')}
              >
                FIBRA
              </Button>
              <Button
                color={tipoInstalacion === 'WIRELESS' ? 'info' : 'secondary'}
                onClick={() => setTipoInstalacion('WIRELESS')}
              >
                WIRELESS
              </Button>
            </>
          ) : null}

          {tipoInstalacion ? (
            <>
              <hr />
              <Row>
                <Col md={8}>
                  <FormGroup>
                    <Label>
                      4) Ingresa la mac del {tipoInstalacion === 'FIBRA' ? 'Router de Fibra' : 'Nano/Cube/Pomo'}
                    </Label>
                    <Input
                      type="text"
                      value={macIngresada}
                      onChange={(e) => setMacIngresada(e.target.value)}
                      placeholder="Ej: 8C86DDD31178"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-center">
                  <Button color="primary" onClick={validarMac}>
                    Validar MAC
                  </Button>
                </Col>
              </Row>
            </>
          ) : null}

          {equipoInstalado ? (
            <>
              <hr />
              <h4>5) Plan seleccionado</h4>
              <p>
                El plan seleccionado es <b>{planActual?.name || `ID ${planIdSeleccionado}`}</b>, deseas cambiarlo?
              </p>
              <Button
                color={deseaCambiarPlan === true ? 'success' : 'secondary'}
                className="mr-2"
                onClick={() => setDeseaCambiarPlan(true)}
              >
                SI
              </Button>
              <Button
                color={deseaCambiarPlan === false ? 'danger' : 'secondary'}
                onClick={() => setDeseaCambiarPlan(false)}
              >
                NO
              </Button>
            </>
          ) : null}

          {deseaCambiarPlan === true ? (
            <Row className="mt-3">
              <Col md={6}>
                <FormGroup>
                  <Label>6) Seleccione el ID del plan al que quiere cambiar</Label>
                  <Input
                    type="select"
                    value={planIdSeleccionado || ''}
                    onChange={(e) => setPlanIdSeleccionado(parseInt(e.target.value, 10))}
                  >
                    <option value="" disabled>Seleccione plan</option>
                    {planes.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {`Id ${plan.id} - ${plan.name}`}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          ) : null}

          {deseaCambiarPlan !== null ? (
            <>
              <hr />
              <h4>7) Confirmacion de datos</h4>
              <p className="mb-1"><b>Nombre:</b> {esperaEvento?.nombre || '-'}</p>
              <p className="mb-1"><b>Apellido:</b> {esperaEvento?.apellido || '-'}</p>
              <p className="mb-1"><b>Telefono:</b> {esperaEvento?.telefono || '-'}</p>
              <p className="mb-1"><b>Dni/Cuit:</b> {esperaEvento?.dni || '-'}</p>
              <p className="mb-1"><b>Direccion:</b> {esperaEvento?.direccion || '-'}</p>
              <p className="mb-1"><b>Email:</b> {esperaEvento?.email || '-'}</p>
              <p className="mb-1"><b>Plan:</b> {planActual?.name || '-'}</p>
              <p className="mb-1"><b>Equipo:</b> {equipoInstalado?.macAddress || '-'}</p>

              <Button color="success" onClick={confirmarAlta}>
                Confirmar
              </Button>
            </>
          ) : null}

          {resultadoFinal ? (
            <div className="alert alert-success mt-3">
              {`Cliente creado: ${resultadoFinal.clienteId}. Servicio creado: ${resultadoFinal.servicioId || '-'}.`}
            </div>
          ) : null}

          {resultadoFinal && tipoInstalacion === 'FIBRA' && devolvioRouter === null && clienteCreado && clienteCreado.id <= 6548 ? (
            <>
              <hr />
              <h4>8) El cliente devolvio el router?</h4>
              <Button
                color="success"
                className="mr-2"
                onClick={() => handleDevolvioRouter(true)}
              >
                SI
              </Button>
              <Button
                color="danger"
                onClick={() => handleDevolvioRouter(false)}
              >
                NO
              </Button>
            </>
          ) : null}

          {resultadoFinal && tipoInstalacion === 'WIRELESS' && utilizoRouterAdicional === null ? (
            <>
              <hr />
              <h4>8) Utilizaste un Router Adicional?</h4>
              <Button
                color="success"
                className="mr-2"
                onClick={() => handleRouterAdicional(true)}
              >
                SI
              </Button>
              <Button
                color="danger"
                onClick={() => handleRouterAdicional(false)}
              >
                NO
              </Button>
            </>
          ) : null}

          {utilizoRouterAdicional === true && !routerAdicionalOk ? (
            <>
              <hr />
              <Row>
                <Col md={8}>
                  <FormGroup>
                    <Label>9) Ingresa la mac del router adicional</Label>
                    <Input
                      type="text"
                      value={macRouterAdicional}
                      onChange={(e) => setMacRouterAdicional(e.target.value)}
                      placeholder="Ej: AA11BB22CC33"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-center">
                  <Button color="primary" onClick={validarMacRouterAdicional}>
                    Validar y entregar
                  </Button>
                </Col>
              </Row>
            </>
          ) : null}

          {deudaCliente !== null && !pagoRegistrado ? (
            <>
              <hr />
              {deudaCliente > 0 ? (
                <>
                  <h4>Como va a abonar?</h4>
                  <p>El cliente debe abonar <b>$ {deudaCliente}</b></p>
                  <Button
                    color={formaPago === 'EFECTIVO' ? 'success' : 'secondary'}
                    className="mr-2"
                    onClick={() => setFormaPago('EFECTIVO')}
                  >
                    EFECTIVO
                  </Button>
                  <Button
                    color={formaPago === 'TARJETA' ? 'info' : 'secondary'}
                    onClick={() => {
                      setFormaPago('TARJETA');
                      setPagoRegistrado(true);
                      infoAlert(setAlert, 'Pago con tarjeta', 'Para abonar con tarjeta ingrese a https://pagos.globalnet.net.ar. Flujo finalizado.');
                    }}
                  >
                    TARJETA
                  </Button>
                </>
              ) : (
                <div className="alert alert-info">Este cliente no tiene deuda. Flujo finalizado.</div>
              )}
            </>
          ) : null}

          {formaPago === 'EFECTIVO' && !pagoRegistrado ? (
            <>
              <hr />
              <p>Cuando hayas recibido el dinero del cliente, presiona el boton:</p>
              <Button color="success" onClick={handlePagoEfectivo}>
                Ya pago
              </Button>
            </>
          ) : null}

          {pagoRegistrado ? (
            <div className="alert alert-success mt-3 mb-0">
              Flujo completo finalizado exitosamente.
            </div>
          ) : null}
        </CardBody>
      </Card>
    </Container>
  );
};

export default InstaladoresBot;
